// Табът „Отчети" на панела на рецепта: в кои разнесени отчети е продадена
// рецептата през периода и колко — сметките, затворени в периода, по отчета, в
// който са влезли (сборът е числото на реда в справката). Клик/Enter на ред
// отваря документа на разнасянето (същият модал като от складовата карта);
// ↑/↓ между редовете.
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import type { SoldRecipeReport } from "../../../../api/inventorySoldAnalysis";
import { fmtDate } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import { formatCount } from "../components/soldReportModel";
import { reportsTotals, nonZeroReports } from "./soldRecipeModel";

// „ПРОДАДЕНО (БР)" се събира на един ред чак в 116px
const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_116px_104px] items-center gap-x-2.5 px-4";

type Props = {
  reports: SoldRecipeReport[] | undefined;
  isLoading: boolean;
  onOpenPosting: (report: SoldRecipeReport) => void;
};

export const SoldRecipeReports = ({ reports: allReports, isLoading, onOpenPosting }: Props) => {
  const { t } = useTranslation();
  if (isLoading || !allReports) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        {isLoading ? <Spin /> : <span className="text-[13px] text-body">{t("inventoryPage.errLoad")}</span>}
      </div>
    );
  }
  // отчет с нулев сбор (продадено и сторнирано) не се показва
  const reports = nonZeroReports(allReports);
  const totals = reportsTotals(reports);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <p className="m-0 border-b border-shade-2 px-4 py-2.5 text-[12.5px] leading-[1.4] text-body">
        {t("inventoryPage.sdReportsHint")}
      </p>
      {reports.length === 0 ? (
        <div className="px-4 py-8 text-center text-[13px] text-body">{t("inventoryPage.sdReportsEmpty")}</div>
      ) : (
        <div onKeyDown={moveRowFocus}>
          <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
            <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.scDoc_report")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.soldColSold")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.colValue")}</span>
          </div>
          {reports.map((report) => {
            const openable = report.postingId != null;
            const label = t("inventoryPage.scDocNumber", {
              label: t("inventoryPage.scDoc_report"),
              number: report.reportNumber ?? report.reportId,
            });
            return (
              <div
                key={report.reportId}
                {...(openable ? openableRowProps(() => onOpenPosting(report)) : {})}
                className={`${ROW_GRID} ${openable ? REPORT_OPENABLE_ROW_CLASS : REPORT_ROW_CLASS}`}
              >
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className={`truncate font-semibold ${openable ? "text-link" : "text-ink"}`}>{label}</span>
                  {report.reportDate && (
                    <span className="shrink-0 font-sheet text-xs text-muted">{fmtDate(report.reportDate)}</span>
                  )}
                </span>
                <span className="text-right font-sheet font-bold tabular-nums text-ink">
                  {formatCount(report.quantity)}
                </span>
                <span className="text-right font-sheet tabular-nums text-ink-2">{formatMoney(report.totalSum)}</span>
              </div>
            );
          })}
          <div className={`${ROW_GRID} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.total")}</span>
            <span className="text-right font-sheet font-bold tabular-nums text-ink">{formatCount(totals.quantity)}</span>
            <span className="text-right font-sheet font-bold tabular-nums text-brand-deep-ink">
              {formatMoney(totals.totalSum)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoldRecipeReports;
